import React, { useState } from 'react';
import { RotateCcw, GitFork, Play, CheckCircle2, AlertTriangle, Loader2, Clock, GitCompare, X } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { MoreHorizontal } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { WriterVersionSummary } from './WriterVersionScrubber';
import WriterDiffView from './WriterDiffView';
import WriterSourceExportButton from './WriterSourceExportButton';
import { useWriterVersionDiff } from '@/hooks/useWriterVersionDiff';


interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  versions: WriterVersionSummary[];
  onRevert?: (v: WriterVersionSummary) => void | Promise<void>;
  onFork?: (v: WriterVersionSummary) => void | Promise<void>;
  onRetryCompile?: (v: WriterVersionSummary) => void | Promise<void>;
  busyVersionId?: string | null;
}

const StatusIcon: React.FC<{ status: WriterVersionSummary['compile_status'] }> = ({ status }) => {
  if (status === 'success') return <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />;
  if (status === 'failed') return <AlertTriangle className="h-3.5 w-3.5 text-destructive" />;
  if (status === 'running') return <Loader2 className="h-3.5 w-3.5 animate-spin text-sky-600 dark:text-sky-400" />;
  return <Clock className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400" />;
};

/**
 * Right-side drawer listing every writer_version for the current project.
 * All per-version actions previously crammed into the header popovers
 * (revert, fork, retry compile) live here — off the primary surface until
 * the author explicitly opens History.
 *
 * "View changes" opens a dedicated diff dialog comparing the row to its
 * predecessor. "Compare with…" starts a pick-a-base mode so the user can
 * diff against any earlier version (e.g. a pinned milestone).
 */
const WriterVersionHistorySheet: React.FC<Props> = ({
  open,
  onOpenChange,
  versions,
  onRevert,
  onFork,
  onRetryCompile,
  busyVersionId,
}) => {
  // When set, we're diffing head against base. `base` may be null to mean
  // "the row before head in creation order" (the natural predecessor).
  const [diff, setDiff] = useState<{ baseId: string | null; headId: string } | null>(null);
  // When non-null, the user clicked "Compare with…"; the next row click picks
  // the base and opens the diff dialog against that head.
  const [pickBaseFor, setPickBaseFor] = useState<string | null>(null);

  const previousVersionId = (headId: string): string | null => {
    const idx = versions.findIndex((v) => v.id === headId);
    if (idx < 0 || idx === versions.length - 1) return null;
    return versions[idx + 1].id;
  };

  const openDiffAgainstPrevious = (headId: string) => {
    setDiff({ baseId: previousVersionId(headId), headId });
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md flex flex-col p-0">
        <SheetHeader className="px-6 py-4 border-b border-border/60">
          <SheetTitle>Version history</SheetTitle>
          <SheetDescription>
            Every compiled draft, newest first. Revert, fork, or view changes.
          </SheetDescription>
        </SheetHeader>
        {pickBaseFor && (
          <div className="px-6 py-2 border-b border-border/60 bg-muted/40 flex items-center gap-2 text-xs">
            <GitCompare className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="text-muted-foreground">
              Pick a version to compare against.
            </span>
            <button
              type="button"
              onClick={() => setPickBaseFor(null)}
              className="ml-auto rounded p-1 hover:bg-background transition"
              aria-label="Cancel compare"
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        )}
        <div className="flex-1 overflow-y-auto">
          {versions.length === 0 ? (
            <p className="px-6 py-10 text-sm text-muted-foreground text-center">
              No versions yet.
            </p>
          ) : (
            <ol className="divide-y divide-border/60">
              {versions.map((v, i) => {
                const isLatest = i === 0;
                const isBusy = busyVersionId === v.id;
                const canRetry =
                  onRetryCompile &&
                  (v.compile_status === 'failed' || v.compile_status === 'pending');
                const canRevert = onRevert && !isLatest;
                const hasPrevious = i < versions.length - 1;
                const isPickTarget = pickBaseFor && pickBaseFor !== v.id;
                let when = '';
                try {
                  when = formatDistanceToNow(new Date(v.created_at), { addSuffix: true });
                } catch {
                  /* noop */
                }
                return (
                  <li
                    key={v.id}
                    className={cn(
                      'px-6 py-3 flex items-start gap-3',
                      isBusy && 'opacity-60',
                      isPickTarget && 'hover:bg-primary/5 cursor-pointer',
                    )}
                    onClick={
                      isPickTarget
                        ? () => {
                            setDiff({ baseId: v.id, headId: pickBaseFor! });
                            setPickBaseFor(null);
                          }
                        : undefined
                    }
                  >
                    <div className="pt-1">
                      <StatusIcon status={v.compile_status} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline gap-2">
                        <span className="text-xs tabular-nums text-muted-foreground">
                          v{versions.length - i}
                        </span>
                        <span className="text-sm font-medium truncate">
                          {v.label ?? 'Untitled'}
                        </span>
                        {isLatest && (
                          <span className="text-[9px] uppercase tracking-widest text-muted-foreground">
                            Latest
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {when}
                        {' · '}
                        <span className="uppercase tracking-widest text-[10px]">
                          {v.compile_status}
                        </span>
                      </p>
                    </div>
                    {!pickBaseFor && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            type="button"
                            aria-label="Version actions"
                            disabled={isBusy}
                            className="rounded-full p-1 hover:bg-muted transition"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <MoreHorizontal className="h-4 w-4" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-52">
                          {hasPrevious && (
                            <DropdownMenuItem onClick={() => openDiffAgainstPrevious(v.id)}>
                              <GitCompare className="h-3.5 w-3.5 mr-2" />
                              View changes
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem onClick={() => setPickBaseFor(v.id)}>
                            <GitCompare className="h-3.5 w-3.5 mr-2 opacity-60" />
                            Compare with…
                          </DropdownMenuItem>
                          {canRetry && (
                            <DropdownMenuItem
                              onClick={() => onRetryCompile?.(v)}
                              disabled={isBusy}
                            >
                              <Play className="h-3.5 w-3.5 mr-2" />
                              Retry compile
                            </DropdownMenuItem>
                          )}
                          {canRevert && (
                            <DropdownMenuItem
                              onClick={() => onRevert?.(v)}
                              disabled={isBusy}
                            >
                              <RotateCcw className="h-3.5 w-3.5 mr-2" />
                              Revert to this
                            </DropdownMenuItem>
                          )}
                          {onFork && (
                            <DropdownMenuItem
                              onClick={() => onFork(v)}
                              disabled={isBusy}
                            >
                              <GitFork className="h-3.5 w-3.5 mr-2" />
                              Fork from here
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem
                            onSelect={(e) => e.preventDefault()}
                            className="p-0 focus:bg-transparent"
                          >
                            <WriterSourceExportButton
                              versionId={v.id}
                              variant="menu"
                              label="Download LaTeX source"
                              className="px-2 py-1.5"
                            />
                          </DropdownMenuItem>

                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      </SheetContent>
      <DiffDialog
        state={diff}
        versions={versions}
        onClose={() => setDiff(null)}
      />
    </Sheet>
  );
};

const DiffDialog: React.FC<{
  state: { baseId: string | null; headId: string } | null;
  versions: WriterVersionSummary[];
  onClose: () => void;
}> = ({ state, versions, onClose }) => {
  const { loading, error, sections } = useWriterVersionDiff(
    state?.baseId ?? null,
    state?.headId ?? null,
  );
  const captionFor = (): string => {
    if (!state) return '';
    const label = (id: string | null) => {
      if (!id) return 'empty';
      const idx = versions.findIndex((v) => v.id === id);
      return idx >= 0 ? `v${versions.length - idx}` : 'unknown';
    };
    return `${label(state.baseId)} → ${label(state.headId)}`;
  };
  return (
    <Dialog open={!!state} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-4xl h-[80vh] p-0 flex flex-col">
        <DialogHeader className="px-6 py-4 border-b border-border/60">
          <DialogTitle>What changed</DialogTitle>
        </DialogHeader>
        <div className="flex-1 min-h-0 flex flex-col">
          {loading && (
            <p className="px-6 py-10 text-center text-sm text-muted-foreground flex items-center justify-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading diff…
            </p>
          )}
          {error && (
            <p className="px-6 py-10 text-center text-sm text-destructive">{error}</p>
          )}
          {!loading && !error && state && (
            <WriterDiffView sections={sections} caption={captionFor()} />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default WriterVersionHistorySheet;
